// Here's the page:
// https://www.figma.com/design/vA2nL7NooVVQji3qsQq98R/Hippo2.0_Journeys?node-id=45359-20611&p=f&t=1WLdbb7fbhEOtbDT-0

import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useMobileContext } from '../../contexts/MobileContext';
import client from '../../utils/contentfulClient';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import { useImagePreload } from '../../hooks/useImagePreload';
import PageShell from '../../components/Layout/PageShell';

// Component Imports:
import SpinOffLaunchPad from '../../components/HomePage/SideCardContent/SpinOffLaunchPad';
import SideCardContent from '../../components/HomePage/SideCardContent/SideCardContentComponent';
import PartnerBrandsComponent from '../../components/HomePage/PartnerBrands/PartnerBrandsComponent';
import PartnerBrandsMobileComponent from '../../components/HomePage/PartnerBrands/PartnerBrandsMobileComponent';
import GenericTextBlockComponent from '../../components/HomePage/GenericTextBlock/GenericTextBlockComponent';
import GenericTextBlockMobileComponent from '../../components/HomePage/GenericTextBlock/GenericTextBlockMobileComponent';
import PartnerLogoGridComponent from '../../components/HomePage/PartnerLogoGrid/PartnerLogoGridComponent';
import PartnerLogoGridMobileComponent from '../../components/HomePage/PartnerLogoGrid/PartnerLogoGridMobileComponent';
import CustomerReviewsComponent from '../../components/HomePage/CustomerReviews/CustomerReviewsComponent';
import CustomerReviewsMobileComponent from '../../components/HomePage/CustomerReviews/CustomerReviewsMobileComponent';
import SectionStepsComponent from '../../components/HomePage/SectionSteps/SectionStepsComponent';
import SectionStepsMobileComponent from '../../components/HomePage/SectionSteps/SectionStepsMobileComponent';
import HippoTwoLineGenericComponent from '../../components/Banners/HippoTwoLineGenericComponent';
import HippoGenericMobileComponent from '../../components/Banners/HippoGenericMobileComponent';
import KeyPointHorizontalNumericComponent from '../../components/HomePage/KeyPointHorizontalNumeric/KeyPointHorizontalNumericComponent';
import KeyPointHorizontalNumericMobileComponent from '../../components/HomePage/KeyPointHorizontalNumeric/KeyPointHorizontalNumericMobileComponent';
import KeyPointVerticalComponent from '../../components/HomePage/KeyPointVertical/KeyPointVerticalComponent';
import KeyPointVerticalMobileComponent from '../../components/HomePage/KeyPointVertical/KeyPointVerticalMobileComponent';
import FourColumnCardsComponent from '../../components/HomePage/FourColumnCards/FourColumnCardsComponent';
import FourColumnCardsMobileComponent from '../../components/HomePage/FourColumnCards/FourColumnCardsMobileComponent';
import AccordionFAQComponent from '../../components/HomePage/AccordionFAQ/AccordionFAQComponent';
import AccordionFAQMobileComponent from '../../components/HomePage/AccordionFAQ/AccordionFAQMobileComponent';
import SeoDetailModuleComponent from '../../components/HomePage/SeoDetailDirtyLoadModule/SeoDetailDirtyLoadModuleComponent';
import SeoDetailModuleMobileComponent from '../../components/HomePage/SeoDetailDirtyLoadModule/SeoDetailDirtyLoadModuleMobileComponent';
import ScrollingCardsComponent from '../../components/HomePage/ScrollingCards/ScrollingCardsComponent';
import ScrollingCardsMobileComponent from '../../components/HomePage/ScrollingCards/ScrollingCardsMobileComponent';
import SeoTagsManager from '../../utils/SeoTagsManager';
import FooterComponent from '../../components/HomePage/Footer/FooterComponent';
import FooterMobileComponent from '../../components/HomePage/Footer/FooterMobileComponent';
import NotFound from '../404/NotFound';
import Error from '../410/Error';
//import DummyPartner from '../../json/components/Other/CarGuideBrand.json';
import SeoSnakeComponent from '../../components/HomePage/SeoSnake/SeoSnakeComponent';
import SeoSnakeMobileComponent from '../../components/HomePage/SeoSnake/SeoSnakeMobileComponent';
import GuidePartnerLaunchPad from '../../components/HomePage/SideCardContent/GuidePartnerLaunchPad';
import SeoDetailBrandModuleComponent from '../../components/HomePage/SeoDetailBrandModule/SeoDetailBrandModuleComponent';
import SeoDetailBrandModuleMobileComponent from '../../components/HomePage/SeoDetailBrandModule/SeoDetailBrandModuleMobileComponent';
import { useLocation } from 'react-router-dom';
import ScrollFadeWrapper from '../../components/ScrollFadeWrapper/ScrollFadeWrapper';
import BreadcrumbComponent from '../../components/Breadcrumb/BreadcrumbComponent';
import { getBreadcrumbs } from '../../utils/breadcrumbConfig';

const CONTENTFUL_PAGE_TYPES = ['spinOffPages', 'partnerPages', 'carGuidePages'];

const normalizeSpinOffConfig = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    try {
      return normalizeSpinOffConfig(JSON.parse(value));
    } catch {
      return [];
    }
  }

  if (!value || typeof value !== 'object') return [];

  const nestedCollections = [
    value.config,
    value.fields?.config,
    value.items,
    value.sections,
    value.content,
  ];

  for (const nested of nestedCollections) {
    const normalizedNested = normalizeSpinOffConfig(nested);
    if (normalizedNested.length > 0) {
      return normalizedNested;
    }
  }

  return value.type ? [value] : [];
};

function resolvePathname(obj, pathname) {
  if (typeof obj === 'string') return obj.replace(/\$\{pathname\}/g, pathname);
  if (Array.isArray(obj)) return obj.map((v) => resolvePathname(v, pathname));
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, resolvePathname(v, pathname)])
    );
  }
  return obj;
}

const isMissingContentTypeError = (err) => {
  const message = String(err?.message || '').toLowerCase();
  const errorId = String(err?.sys?.id || '').toLowerCase();

  return (
    errorId.includes('contenttype') ||
    (message.includes('content type') &&
      (message.includes('unknown') ||
        message.includes('not found') ||
        message.includes('does not exist')))
  );
};

const findPageEntry = async (urlCandidates) => {
  for (const contentType of CONTENTFUL_PAGE_TYPES) {
    for (const candidate of urlCandidates) {
      try {
        const entries = await client.getEntries({
          content_type: contentType,
          'fields.url': candidate,
        });

        if (entries.items[0]) {
          return entries.items[0];
        }
      } catch (err) {
        if (isMissingContentTypeError(err)) {
          // Keep fallback support tolerant while new models are being rolled out.
          break;
        }

        throw err;
      }
    }
  }

  return null;
};

const SpinOff = () => {
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const isMobile = useMobileContext();
  const { pathname } = useLocation();

  useEffect(() => {
    const normalisedPath = pathname.replace(/\/+$/, '');
    const pathParts = normalisedPath.split('/').filter(Boolean);
    const lastSegment = pathParts[pathParts.length - 1] || '';
    const nestedPath = pathParts.slice(1).join('/');

    const withoutLeadingSlash = normalisedPath.replace(/^\/+/, '');

    const urlCandidates = [
      lastSegment,
      `${lastSegment}/`,
      nestedPath,
      `${nestedPath}/`,
      withoutLeadingSlash,
      `${withoutLeadingSlash}/`,
      `/${nestedPath}`,
      `/${nestedPath}/`,
      `/${withoutLeadingSlash}`,
      `/${withoutLeadingSlash}/`,
    ].filter(Boolean);

    const uniqueCandidates = [...new Set(urlCandidates)];

    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const entry = await findPageEntry(uniqueCandidates);

        if (entry) {
          setContent(entry.fields?.config ?? null);
          return;
        }

        setContent(null);
        // const dummyConfig = Array.isArray(DummyPartner)
        //   ? DummyPartner
        //   : (DummyPartner.config ?? DummyPartner.fields?.config ?? null);
        // setContent(dummyConfig);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [pathname]);

  // normalize isMobile / isDesktop at top of your component
  // (place inside the component function)
  const isDesktop = Boolean(isMobile.isDesktop);
  const normalizedContent = useMemo(
    () => normalizeSpinOffConfig(content),
    [content]
  );
  const heroImageSources = useMemo(() => {
    if (!Array.isArray(normalizedContent)) return [];

    return normalizedContent
      .filter(
        (item) =>
          item?.type === 'launch-pad' ||
          item?.type === 'launch-pad-guide-partner'
      )
      .flatMap((item) => (item?.image ? [item.image] : []));
  }, [normalizedContent]);
  // Collect FAQ entries from the page config (e.g. accordion-faq sections) so
  // they can be emitted as FAQPage structured data via the seo-tags item.
  const seoFaqs = useMemo(() => {
    if (!Array.isArray(normalizedContent)) return [];

    return normalizedContent
      .filter((item) => Array.isArray(item?.faq))
      .flatMap((item) => item.faq)
      .filter((faq) => faq && (faq.title || faq.description));
  }, [normalizedContent]);
  const areHeroImagesReady = useImagePreload(heroImageSources);
  const pageHeading = normalizedContent?.[0]?.heading;
  const breadcrumbItems = useMemo(
    () => getBreadcrumbs(pathname, pageHeading),
    [pathname, pageHeading]
  );

  if (loading)
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}
      >
        <LoadingDots variant="otp" />
      </div>
    );

  if (error) return <Error />; //<div>Error: {error}</div>;
  if (!Array.isArray(normalizedContent) || normalizedContent.length === 0)
    return <NotFound />;
  if (!areHeroImagesReady)
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}
      >
        <LoadingDots variant="otp" />
      </div>
    );

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: 2, sm: 3, md: 4 },
          pb: 0,
          minHeight: '100vh',
          overflowX: 'clip',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: '#F3F6F6',
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <PageShell
            containerSx={
              breadcrumbItems ? { mt: { xs: 0.5, sm: 4, md: 4 } } : {}
            }
          >
            {breadcrumbItems && (
              <Box
                sx={{
                  maxWidth: '1113px',
                  pl: { xs: '16px', md: '24px' },
                  mb: { xs: '16px', sm: '12px', md: '12px' },
                }}
              >
                <BreadcrumbComponent items={breadcrumbItems} />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '24px', sm: '48px', md: '64px' },
              }}
            >
              {/* Launch Pad + sections */}
              {/* Put back once contentful is working */}
              {normalizedContent.map((item, index) => {
                if (item.type === 'launch-pad') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      <SpinOffLaunchPad {...item} isMobile={!isDesktop} />
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'launch-pad-guide-partner') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      <GuidePartnerLaunchPad {...item} isMobile={!isDesktop} />
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'sidecar-content') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      <SideCardContent {...item} isMobile={!isDesktop} />
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'partner-brands') {
                  const normalisedItem = {
                    ...item,
                    logos: (item.logos || []).map((l) =>
                      typeof l === 'string' ? { logo: l } : l
                    ),
                  };

                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <PartnerBrandsComponent data={normalisedItem} />
                      ) : (
                        <PartnerBrandsMobileComponent data={normalisedItem} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'generic-text-block') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <GenericTextBlockComponent data={item} />
                      ) : (
                        <GenericTextBlockMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'partner-logo-grid') {
                  const normalisedItem = {
                    ...item,
                    cards: (item.cards || []).map((card) =>
                      typeof card === 'string' ? { logo: card } : card
                    ),
                  };

                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <PartnerLogoGridComponent data={normalisedItem} />
                      ) : (
                        <PartnerLogoGridMobileComponent data={normalisedItem} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'customer-reviews') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <CustomerReviewsComponent data={item} />
                      ) : (
                        <CustomerReviewsMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'seo-snake') {
                  return (
                    <React.Fragment key={index}>
                      {isDesktop ? (
                        <SeoSnakeComponent data={item} />
                      ) : (
                        <SeoSnakeMobileComponent data={item} />
                      )}
                    </React.Fragment>
                  );
                }

                if (item.type === 'section-steps') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <SectionStepsComponent data={item} />
                      ) : (
                        <SectionStepsMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'generic-banner') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <HippoTwoLineGenericComponent
                          route={item.route}
                          image={item.image}
                          title={item.title}
                          buttonText={item.buttonText}
                          color={item.colour}
                        />
                      ) : (
                        <HippoGenericMobileComponent
                          route={item.route}
                          title={item.title}
                          image={item.image}
                          buttonText={item.buttonText}
                          color={item.colour}
                        />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'keypoint-vertical') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <KeyPointVerticalComponent data={item} />
                      ) : (
                        <KeyPointVerticalMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'keypoint-horizontal') {
                  const normalisedItem = {
                    ...item,
                    cards: (item.cards || []).map((card, cardIndex) => {
                      if (typeof card === 'string') {
                        return {
                          number: String(cardIndex + 1),
                          text: card,
                        };
                      }

                      return {
                        ...card,
                        number: String(card.number ?? cardIndex + 1),
                        text: card.text || '',
                      };
                    }),
                  };

                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <KeyPointHorizontalNumericComponent
                          data={normalisedItem}
                        />
                      ) : (
                        <KeyPointHorizontalNumericMobileComponent
                          data={normalisedItem}
                        />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'four-column-cards') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <FourColumnCardsComponent data={item} />
                      ) : (
                        <FourColumnCardsMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'accordion-faq') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <AccordionFAQComponent data={item} />
                      ) : (
                        <AccordionFAQMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'scrolling-cards') {
                  const normalisedItem = {
                    ...item,
                    cards: (item.cards || []).map((card) => ({
                      ...card,
                      icon:
                        typeof card.icon === 'string' &&
                        card.icon.startsWith('http')
                          ? card.icon
                          : '',
                    })),
                  };

                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <ScrollingCardsComponent data={normalisedItem} />
                      ) : (
                        <ScrollingCardsMobileComponent data={normalisedItem} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'seo-detail') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <SeoDetailModuleComponent data={item} />
                      ) : (
                        <SeoDetailModuleMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'seo-detail-brand') {
                  return (
                    <ScrollFadeWrapper key={index}>
                      {isDesktop ? (
                        <SeoDetailBrandModuleComponent data={item} />
                      ) : (
                        <SeoDetailBrandModuleMobileComponent data={item} />
                      )}
                    </ScrollFadeWrapper>
                  );
                }

                if (item.type === 'seo-tags') {
                  const identifier = item.identifier || pathname;
                  const resolvedItem = resolvePathname(item, pathname);
                  // Feed any FAQs from the page config into the seo-tags config so
                  // SeoTagsManager can emit them as FAQPage structured data.
                  const seoItem = seoFaqs.length
                    ? {
                        ...resolvedItem,
                        faqs: [...(resolvedItem.faqs || []), ...seoFaqs],
                      }
                    : resolvedItem;
                  return (
                    <React.Fragment key={index}>
                      {SeoTagsManager.render(identifier, seoItem)}
                    </React.Fragment>
                  );
                }

                return null;
              })}
            </Box>
          </PageShell>

          {/* Footer stays below the page shell so it can anchor to the bottom */}
          <Box sx={{ mt: 'auto' }}>
            {isDesktop ? <FooterComponent /> : <FooterMobileComponent />}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SpinOff;
